import React, { useCallback, useState, useEffect } from "react";
import { Document, Page } from "react-pdf";
import { cn } from "@/shared/utils/core";
import {
  ChevronLeft,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  Printer,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import "@/shared/utils/pdf-worker";
import { Button } from "@/shared/components/ui/button";
import { IconButton } from "@/shared/components/ui/icon-button";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";

const TPA_PDF_URL = "/tpa-authorization.pdf";

interface TPADocumentViewerProps {
  isOpen: boolean;
  onClose: () => void;
  pdfUrl?: string;
}

export default function TPADocumentViewer({ isOpen, onClose, pdfUrl }: TPADocumentViewerProps) {
  const documentUrl = pdfUrl || TPA_PDF_URL;
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [loadError, setLoadError] = useState<boolean>(false);
  const [isMobile, setIsMobile] = useState<boolean>(false);
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  // Get device pixel ratio for high-quality rendering
  const devicePixelRatio = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;

  // Track container width and mobile state
  useEffect(() => {
    const updateDimensions = () => {
      setIsMobile(window.innerWidth < 640);
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth);
      }
    };

    if (isOpen) {
      updateDimensions();
      setTimeout(updateDimensions, 50);
      setTimeout(updateDimensions, 150);
    }

    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, [isOpen]);

  // Reset state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setPageNumber(1);
      // Set default zoom to 80% on mobile, 100% on desktop
      const mobile = typeof window !== "undefined" && window.innerWidth < 640;
      setScale(mobile ? 0.8 : 1.0);
      setLoadError(false);
    }
  }, [isOpen]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    console.error("[TPA PDF] Failed to load PDF:", error.message);
    setLoadError(true);
  }, []);

  const goToPrevPage = useCallback(() => {
    setPageNumber((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(prev + 1, numPages));
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale((s) => Math.min(s + 0.2, 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((s) => Math.max(s - 0.2, 0.5));
  }, []);

  const handlePrint = useCallback(() => {
    const printWindow = window.open(documentUrl, "_blank");
    if (printWindow) {
      printWindow.onload = () => {
        printWindow.print();
      };
    }
  }, [documentUrl]);

  const handleDownload = useCallback(async () => {
    try {
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Third-Party-Authorization.pdf";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch {
      window.open(documentUrl, "_blank");
    }
  }, [documentUrl]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="!w-[100vw] !h-[100dvh] !max-w-[100vw] sm:!w-[95vw] sm:!h-[92vh] sm:!max-w-7xl p-0 flex flex-col rounded-none sm:rounded-lg border-0 sm:border">
        {/* Header with controls */}
        <div className="flex items-center justify-between px-2 py-2 sm:px-4 sm:py-3 bg-white border-b border-stone-200">
          {/* Left side - Page navigation */}
          <div className="flex items-center gap-1 sm:gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="h-9 w-9 sm:h-10 sm:w-10 disabled:opacity-40"
              aria-label="Previous page"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>

            <span className="text-sm sm:text-base font-medium min-w-[60px] sm:min-w-[80px] text-center">
              {pageNumber} / {numPages}
            </span>

            <Button
              variant="outline"
              size="icon"
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="h-9 w-9 sm:h-10 sm:w-10 disabled:opacity-40"
              aria-label="Next page"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>

          {/* Center - Zoom controls (hidden on mobile since page auto-fits) */}
          <div className="hidden sm:flex items-center gap-1 sm:gap-2">
            <IconButton size="md" onClick={zoomOut} label="Zoom out">
              <ZoomOut />
            </IconButton>
            <span className="text-sm sm:text-sm font-medium w-10 sm:w-12 text-center">
              {Math.round(scale * 100)}%
            </span>
            <IconButton size="md" onClick={zoomIn} label="Zoom in">
              <ZoomIn />
            </IconButton>
          </div>

          {/* Right side - Action buttons */}
          <div className="flex items-center gap-1 sm:gap-2">
            <IconButton size="md" onClick={handleDownload} label="Download">
              <Download />
            </IconButton>
            <IconButton
              size="md"
              onClick={handlePrint}
              className="hidden sm:inline-flex"
              label="Print"
            >
              <Printer />
            </IconButton>
            <IconButton size="md" onClick={onClose} label="Close" tooltip={false}>
              <X />
            </IconButton>
          </div>
        </div>

        {/* PDF Viewer */}
        <div
          ref={containerRef}
          className="flex-1 overflow-auto bg-stone-100 min-h-0 flex items-center justify-center"
        >
          <div className="flex justify-center py-1 sm:p-4">
            {loadError ? (
              <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                <FileText size={48} className="mb-2" />
                <p>Failed to load PDF</p>
                <a
                  href={documentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download="Third-Party-Authorization.pdf"
                  className="mt-2 flex items-center text-blue-600 hover:text-blue-800"
                >
                  <Download className="mr-1 w-4 h-4" /> Download instead
                </a>
              </div>
            ) : (
              <Document
                file={documentUrl}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={
                  <div className="flex flex-col items-center justify-center h-[60vh] sm:h-[50vh] gap-4">
                    <Loader2 className="h-10 w-10 text-teal-600 animate-spin" />
                    <div className="text-stone-600 font-medium">Loading document...</div>
                  </div>
                }
                error={
                  <div className="flex flex-col items-center justify-center h-64 text-gray-500">
                    <FileText size={48} className="mb-2" />
                    <p>Failed to load PDF</p>
                    <a
                      href={documentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download="Third-Party-Authorization.pdf"
                      className="mt-2 flex items-center text-blue-600 hover:text-blue-800"
                    >
                      <Download className="mr-1 w-4 h-4" /> Download instead
                    </a>
                  </div>
                }
              >
                <Page
                  pageNumber={pageNumber}
                  scale={isMobile ? undefined : scale}
                  width={isMobile && containerWidth > 0 ? containerWidth : undefined}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                  className={cn("shadow-lg bg-white", isMobile && "shadow-none")}
                  canvasBackground="#ffffff"
                  devicePixelRatio={Math.max(devicePixelRatio, 2)}
                />
              </Document>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
